/**
 * Daily lectionary - minimal Phase 3 coverage.
 *
 * The Orthodox lectionary has two interleaving cycles:
 *
 *   1. The movable cycle (Pascha-anchored), daily epistle + gospel
 *      readings that walk through the New Testament from Pascha to Pascha.
 *   2. The fixed-day cycle - readings tied to month/day for major feasts
 *      and saint commemorations.
 *
 * Phase 3 ships the framework plus the readings for the major feasts and
 * the Sundays of the Pentecostarion (the Bright period through Pentecost).
 * The full daily lectionary for ordinary weekdays is deferred to a content
 * authoring pass - engine semantics are stable, only the data grows.
 */
import { diffDays } from './dates.js';
/** Movable readings keyed by `daysOffsetFromPascha`. */
const MOVABLE = {
    // Pascha
    '0': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 1:1-17',
            chapterStart: 1,
            verseStart: 1,
            chapterEnd: 1,
            verseEnd: 17,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 1:1-8',
            chapterStart: 1,
            verseStart: 1,
            chapterEnd: 1,
            verseEnd: 8,
        },
    ],
    // Bright Monday
    '1': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 1:18-28',
            chapterStart: 1,
            verseStart: 18,
            chapterEnd: 1,
            verseEnd: 28,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 1:12-17, 21-26',
            chapterStart: 1,
            verseStart: 12,
        },
    ],
    // Thomas Sunday (Antipascha)
    '7': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 20:19-31',
            chapterStart: 20,
            verseStart: 19,
            chapterEnd: 20,
            verseEnd: 31,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 5:12-20',
            chapterStart: 5,
            verseStart: 12,
            chapterEnd: 5,
            verseEnd: 20,
        },
    ],
    // Sunday of the Myrrh-bearing Women
    '14': [
        {
            type: 'gospel',
            bookSlug: 'mark',
            reference: 'Mark 15:43-16:8',
            chapterStart: 15,
            verseStart: 43,
            chapterEnd: 16,
            verseEnd: 8,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 6:1-7',
            chapterStart: 6,
            verseStart: 1,
            chapterEnd: 6,
            verseEnd: 7,
        },
    ],
    // Sunday of the Paralytic
    '21': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 5:1-15',
            chapterStart: 5,
            verseStart: 1,
            chapterEnd: 5,
            verseEnd: 15,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 9:32-42',
            chapterStart: 9,
            verseStart: 32,
            chapterEnd: 9,
            verseEnd: 42,
        },
    ],
    // Mid-Pentecost
    '24': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 7:14-30',
            chapterStart: 7,
            verseStart: 14,
            chapterEnd: 7,
            verseEnd: 30,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 14:6-18',
            chapterStart: 14,
            verseStart: 6,
            chapterEnd: 14,
            verseEnd: 18,
        },
    ],
    // Sunday of the Samaritan Woman
    '28': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 4:5-42',
            chapterStart: 4,
            verseStart: 5,
            chapterEnd: 4,
            verseEnd: 42,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 11:19-26, 29-30',
            chapterStart: 11,
            verseStart: 19,
        },
    ],
    // Sunday of the Blind Man
    '35': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 9:1-38',
            chapterStart: 9,
            verseStart: 1,
            chapterEnd: 9,
            verseEnd: 38,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 16:16-34',
            chapterStart: 16,
            verseStart: 16,
            chapterEnd: 16,
            verseEnd: 34,
        },
    ],
    // Ascension
    '39': [
        {
            type: 'gospel',
            bookSlug: 'luke',
            reference: 'Luke 24:36-53',
            chapterStart: 24,
            verseStart: 36,
            chapterEnd: 24,
            verseEnd: 53,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 1:1-12',
            chapterStart: 1,
            verseStart: 1,
            chapterEnd: 1,
            verseEnd: 12,
        },
    ],
    // Sunday of the Holy Fathers (week 6, between Ascension and Pentecost)
    '42': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 17:1-13',
            chapterStart: 17,
            verseStart: 1,
            chapterEnd: 17,
            verseEnd: 13,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 20:16-18, 28-36',
            chapterStart: 20,
            verseStart: 16,
        },
    ],
    // Pentecost
    '49': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 7:37-52; 8:12',
            chapterStart: 7,
            verseStart: 37,
            chapterEnd: 8,
            verseEnd: 12,
        },
        {
            type: 'epistle',
            bookSlug: 'acts',
            reference: 'Acts 2:1-11',
            chapterStart: 2,
            verseStart: 1,
            chapterEnd: 2,
            verseEnd: 11,
        },
    ],
    // All Saints Sunday
    '56': [
        {
            type: 'gospel',
            bookSlug: 'matthew',
            reference: 'Matthew 10:32-33, 37-38; 19:27-30',
            chapterStart: 10,
            verseStart: 32,
        },
        {
            type: 'epistle',
            bookSlug: 'hebrews',
            reference: 'Hebrews 11:33-12:2',
            chapterStart: 11,
            verseStart: 33,
            chapterEnd: 12,
            verseEnd: 2,
        },
    ],
    // Palm Sunday
    '-7': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 12:1-18',
            chapterStart: 12,
            verseStart: 1,
            chapterEnd: 12,
            verseEnd: 18,
        },
        {
            type: 'epistle',
            bookSlug: 'philippians',
            reference: 'Philippians 4:4-9',
            chapterStart: 4,
            verseStart: 4,
            chapterEnd: 4,
            verseEnd: 9,
        },
    ],
    // Lazarus Saturday
    '-8': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 11:1-45',
            chapterStart: 11,
            verseStart: 1,
            chapterEnd: 11,
            verseEnd: 45,
        },
        {
            type: 'epistle',
            bookSlug: 'hebrews',
            reference: 'Hebrews 12:28-13:8',
            chapterStart: 12,
            verseStart: 28,
            chapterEnd: 13,
            verseEnd: 8,
        },
    ],
    // Great and Holy Friday, twelve passion gospels combined; principal one:
    '-2': [
        {
            type: 'gospel',
            bookSlug: 'matthew',
            reference: 'Matthew 27:1-56',
            chapterStart: 27,
            verseStart: 1,
            chapterEnd: 27,
            verseEnd: 56,
        },
    ],
};
/** Fixed readings keyed by `MM-DD`. */
const FIXED = {
    '01-06': [
        {
            type: 'gospel',
            bookSlug: 'matthew',
            reference: 'Matthew 3:13-17',
            chapterStart: 3,
            verseStart: 13,
            chapterEnd: 3,
            verseEnd: 17,
        },
        {
            type: 'epistle',
            bookSlug: 'titus',
            reference: 'Titus 2:11-14; 3:4-7',
            chapterStart: 2,
            verseStart: 11,
        },
    ],
    '02-02': [
        {
            type: 'gospel',
            bookSlug: 'luke',
            reference: 'Luke 2:22-40',
            chapterStart: 2,
            verseStart: 22,
            chapterEnd: 2,
            verseEnd: 40,
        },
        {
            type: 'epistle',
            bookSlug: 'hebrews',
            reference: 'Hebrews 7:7-17',
            chapterStart: 7,
            verseStart: 7,
            chapterEnd: 7,
            verseEnd: 17,
        },
    ],
    '03-25': [
        {
            type: 'gospel',
            bookSlug: 'luke',
            reference: 'Luke 1:24-38',
            chapterStart: 1,
            verseStart: 24,
            chapterEnd: 1,
            verseEnd: 38,
        },
        {
            type: 'epistle',
            bookSlug: 'hebrews',
            reference: 'Hebrews 2:11-18',
            chapterStart: 2,
            verseStart: 11,
            chapterEnd: 2,
            verseEnd: 18,
        },
    ],
    '06-29': [
        {
            type: 'gospel',
            bookSlug: 'matthew',
            reference: 'Matthew 16:13-19',
            chapterStart: 16,
            verseStart: 13,
            chapterEnd: 16,
            verseEnd: 19,
        },
        {
            type: 'epistle',
            bookSlug: '2-corinthians',
            reference: '2 Corinthians 11:21-12:9',
            chapterStart: 11,
            verseStart: 21,
            chapterEnd: 12,
            verseEnd: 9,
        },
    ],
    '08-06': [
        {
            type: 'gospel',
            bookSlug: 'matthew',
            reference: 'Matthew 17:1-9',
            chapterStart: 17,
            verseStart: 1,
            chapterEnd: 17,
            verseEnd: 9,
        },
        {
            type: 'epistle',
            bookSlug: '2-peter',
            reference: '2 Peter 1:10-19',
            chapterStart: 1,
            verseStart: 10,
            chapterEnd: 1,
            verseEnd: 19,
        },
    ],
    '08-15': [
        {
            type: 'gospel',
            bookSlug: 'luke',
            reference: 'Luke 10:38-42; 11:27-28',
            chapterStart: 10,
            verseStart: 38,
        },
        {
            type: 'epistle',
            bookSlug: 'philippians',
            reference: 'Philippians 2:5-11',
            chapterStart: 2,
            verseStart: 5,
            chapterEnd: 2,
            verseEnd: 11,
        },
    ],
    '09-08': [
        {
            type: 'gospel',
            bookSlug: 'luke',
            reference: 'Luke 10:38-42; 11:27-28',
            chapterStart: 10,
            verseStart: 38,
        },
        {
            type: 'epistle',
            bookSlug: 'philippians',
            reference: 'Philippians 2:5-11',
            chapterStart: 2,
            verseStart: 5,
            chapterEnd: 2,
            verseEnd: 11,
        },
    ],
    '09-14': [
        {
            type: 'gospel',
            bookSlug: 'john',
            reference: 'John 19:6-11, 13-20, 25-28, 30-35',
            chapterStart: 19,
            verseStart: 6,
        },
        {
            type: 'epistle',
            bookSlug: '1-corinthians',
            reference: '1 Corinthians 1:18-24',
            chapterStart: 1,
            verseStart: 18,
            chapterEnd: 1,
            verseEnd: 24,
        },
    ],
    '11-21': [
        {
            type: 'gospel',
            bookSlug: 'luke',
            reference: 'Luke 10:38-42; 11:27-28',
            chapterStart: 10,
            verseStart: 38,
        },
        {
            type: 'epistle',
            bookSlug: 'hebrews',
            reference: 'Hebrews 9:1-7',
            chapterStart: 9,
            verseStart: 1,
            chapterEnd: 9,
            verseEnd: 7,
        },
    ],
    '12-25': [
        {
            type: 'gospel',
            bookSlug: 'matthew',
            reference: 'Matthew 2:1-12',
            chapterStart: 2,
            verseStart: 1,
            chapterEnd: 2,
            verseEnd: 12,
        },
        {
            type: 'epistle',
            bookSlug: 'galatians',
            reference: 'Galatians 4:4-7',
            chapterStart: 4,
            verseStart: 4,
            chapterEnd: 4,
            verseEnd: 7,
        },
    ],
};
/**
 * Resolve the day's readings. If both fixed and movable readings apply (e.g.
 * Annunciation falls on a Sunday of Lent), both lists are returned with the
 * fixed-feast readings first.
 */
export function readingsForDate(date, paschaForYear) {
    const out = [];
    const mmdd = `${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
    const fixed = FIXED[mmdd];
    if (fixed)
        out.push(...fixed);
    const offset = diffDays(paschaForYear, date);
    const movable = MOVABLE[String(offset)];
    if (movable)
        out.push(...movable);
    return out;
}
//# sourceMappingURL=readings.js.map